import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';

import { catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';

import { AppInsightsBaseService } from '@kros-sk/core/application-insights';
import { IGNORE_REQUEST_ERROR_CODES } from '@kros-sk/core/ignore-request-errors';
import { ToastService } from '@kros-sk/ui/toast';

import { handleProgressEventFailed } from '../utils/http-error-handling.interceptor.utils';

// do not add ANY new error statuses!!!
const allowedErrorCodes = [404, 409];

/**
 * Functional variant of HttpErrorHandlingInterceptor.
 */
export const HttpErrorHandlingInterceptorFn: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const appInsights = inject(AppInsightsBaseService);
  const toastService = inject(ToastService);

  const request = req.clone({ headers: getHeaders(req) });

  return next(request).pipe(catchError((err) => handleHttpError(appInsights, err, allowedErrorCodes, toastService, req)));
};

/**
 * Returns headers without kros-handle-status and kros-no-connection-id headers.
 */
function getHeaders(req: HttpRequest<any>): any {
  return req.headers.delete('kros-handle-status').delete('kros-no-connection-id');
}

function handleHttpError(
  appInsights: AppInsightsBaseService,
  error: HttpErrorResponse,
  allowedErrorCodes: number[],
  toastService: ToastService,
  req: HttpRequest<any>
): Observable<any> {
  if (!ignoreError(error.status, req)) {
    // errors ignored using decorators on api services and its methods
    if (!allowedErrorCodes.includes(error.status)) {
      // error ignored globally - not good idea!
      if (error.status === 402) {
        trackError(appInsights, req, error, 'license');
        toastService.show('Váš balík sa nepodarilo overiť. Skúste znova načítať túto stránku.', {
          classname: 'kros-toast kros-warning',
        });
        console.log('Current license is insufficient for this action', error.error.message);
      } else if (error.error instanceof ErrorEvent) {
        trackError(appInsights, req, error, 'client');
        toastService.show('Chyba siete alebo klientskej aplikácie. ', { classname: 'kros-toast kros-warning' });
        console.log('Client side or network error occurred', error.error.message);
      } else if (error.status === 0) {
        handleProgressEventFailed(appInsights, toastService, error, req);
      } else {
        trackError(appInsights, req, error, 'server');
        toastService.show('Spojenie so serverom zlyhalo. Skúste to znovu.\n', { classname: 'kros-toast kros-warning' });
        console.log('Backend returned an unsuccessful response code', `Status code: ${error.status},` + `Body: ${error.error}, req url: ${req.url}`);
      }
    } else {
      trackError(appInsights, req, error, 'none - allowed');
    }
  } else {
    trackError(appInsights, req, error, 'none - custom');
  }
  return throwError(() => error);
}

function trackError(appInsights: AppInsightsBaseService, req: HttpRequest<any>, error: HttpErrorResponse, errorMessage: string): void {
  const properties = {
    errorMessage,
    errorStatus: error.status,
    requestUrl: req?.url,
    requestMethod: req?.method,
    errors: error?.error?.errors ?? error?.error,
    requestBody: '',
  };
  // log body of patch request, to better understand why it failed
  if (error.status === 400 && req?.body && req?.method === 'PATCH') {
    properties.requestBody = req.body;
  }
  appInsights.trackException(error, 'HttpErrorHandlingInterceptor', properties);
}

/**
 * Method to determine if error has to be ignored by interceptor - to not show any error message for user.
 * It's because specific decorator has been used and ignored error codes were specified.
 */
function ignoreError(errorStatus: number, req: HttpRequest<any>): boolean {
  const ignoredStatusListFromContext = req.context.get(IGNORE_REQUEST_ERROR_CODES) ?? [];
  const ignoredStatusListFromHeaders: string[] = req.headers.getAll('kros-handle-status') ?? [];
  const ignoredStatusList = [...ignoredStatusListFromHeaders, ...ignoredStatusListFromContext];
  if (ignoredStatusList && ignoredStatusList.includes('*')) return true; // all statuses are ignored
  return ignoredStatusList && ignoredStatusList.includes(errorStatus.toString());
}
